import React from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, TrendingDown, Users, Activity } from 'lucide-react';
import { getWeaponIcon } from '../utils/weaponIcons';

const SkinCard = ({ skin, showRankChange = false }) => {
  const navigate = useNavigate();

  const handleClick = () => {
    if (skin.id) {
      navigate(`/skin/${skin.id}`);
    }
  };
  const getRarityClass = (rarity) => {
    const rarityMap = {
      'Consumer Grade': 'rarity-consumer',
      'Industrial Grade': 'rarity-industrial',
      'Mil-Spec Grade': 'rarity-milspec',
      'Restricted': 'rarity-restricted',
      'Classified': 'rarity-classified',
      'Covert': 'rarity-covert'
    };
    return rarityMap[rarity] || 'border-l-4 border-gray-500';
  };

  const getRankBadgeClass = (rank) => {
    if (rank === 1) return 'rank-1';
    if (rank === 2) return 'rank-2';
    if (rank === 3) return 'rank-3';
    return 'rank-default';
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  return (
    <div 
      className={`skin-card ${getRarityClass(skin.rarity)} group cursor-pointer transition-transform hover:scale-105`}
      onClick={handleClick}
    >
      {/* Rank Badge */}
      <div className="flex items-center justify-between mb-3">
        <div className={getRankBadgeClass(skin.rank)}>
          #{skin.rank}
        </div>
        {showRankChange && skin.rank_change && (
          <div className={`flex items-center space-x-1 text-sm ${
            skin.rank_change > 0 ? 'text-green-400' : 'text-red-400'
          }`}>
            {skin.rank_change > 0 ? (
              <TrendingUp className="h-4 w-4" />
            ) : (
              <TrendingDown className="h-4 w-4" />
            )}
            <span>{Math.abs(skin.rank_change)}</span>
          </div>
        )}
      </div>

      {/* Skin Image */}
      <div className="relative mb-4 overflow-hidden rounded-lg bg-gray-700 aspect-video">
        {skin.image_url ? (
          <img
            src={skin.image_url}
            alt={skin.skin_name}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
            onError={(e) => {
              // Hide image and show fallback if it fails to load
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
        ) : null}
        {/* Fallback block when no image or image fails */}
        <div 
          className={`absolute inset-0 flex items-center justify-center text-gray-400 text-sm p-4 text-center ${skin.image_url ? 'hidden' : 'flex'}`}
          style={{ display: skin.image_url ? 'none' : 'flex' }}
        >
          <div>
            <div className="text-3xl mb-2">{getWeaponIcon(skin.weapon_name)}</div>
            <div className="font-medium text-sm">{skin.weapon_name}</div>
            <div className="text-xs opacity-75 mt-1">{skin.skin_name}</div>
          </div>
        </div>
        <div className="absolute top-2 right-2">
          <span className="bg-black bg-opacity-70 text-xs px-2 py-1 rounded text-white">
            {skin.category}
          </span>
        </div>
      </div>

      {/* Skin Info */}
      <div className="space-y-3">
        <div>
          <h3 className="font-semibold text-white text-lg leading-tight group-hover:text-primary-400 transition-colors">
            {skin.skin_name}
          </h3>
          {skin.rarity && skin.rarity.toLowerCase() !== 'unknown' && (
            <p className="text-sm text-gray-400">{skin.rarity}</p>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="flex items-center space-x-2 text-gray-300">
            <Activity className="h-4 w-4 text-primary-400" />
            <span>{skin.usage_count.toLocaleString()} uses</span>
          </div>
          <div className="flex items-center space-x-2 text-gray-300">
            <Users className="h-4 w-4 text-green-400" />
            <span>{skin.unique_players} players</span>
          </div>
        </div>

        {/* Price and Score */}
        <div className="flex items-center justify-between pt-2 border-t border-gray-700">
          <div className="text-sm">
            <span className="text-gray-400">Score: </span>
            <span className="text-primary-400 font-semibold">
              {skin.popularity_score}
            </span>
          </div>
          {skin.avg_price && (
            <div className="text-sm font-semibold text-green-400">
              {formatPrice(skin.avg_price)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SkinCard;
